import React from 'react'
import type { WorkspaceClientState } from '@/api/client'

/**
 * Honest surface for a workspace that is stuck, was never built, or is being
 * reworked after a failure.
 *
 * The motivating incident: a pilot client opened a silently-broken workspace
 * draft, saw a card that never changed and no message anywhere, and left. A
 * stalled workspace must always say what happened and what to do next.
 *
 * The API writes `message`/`action` in client-safe plain language, so this
 * component renders them verbatim and never invents copy of its own.
 */
export default function WorkspaceStateNotice({
  state,
  variant = 'card',
}: {
  state?: WorkspaceClientState | null
  variant?: 'card' | 'banner'
}) {
  if (!state || !state.message) return null

  const reworked = state.kind === 'being_reworked'
  const label = reworked ? 'Being reworked' : 'Needs attention'

  return (
    <div
      className={`workspace-state-notice ${reworked ? 'is-rework' : 'is-stalled'} ${
        variant === 'banner' ? 'is-banner' : ''
      }`}
      role="status"
      aria-live="polite"
    >
      <span className={`pill ${reworked ? 'bad' : 'warn'}`}>{label}</span>
      <span className="workspace-state-notice-text">
        {state.message}
        {state.action ? <em> {state.action}</em> : null}
      </span>
    </div>
  )
}
